import React, { useEffect } from "react";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import getConfig from "next/config";
import { NextSeo } from "next-seo";
import {
    PageLayout, JobSearchRequest, JobSearchResults, DirectApply
} from "@themuse/design-system";

import { AppProvider, useAppState, useAppDispatch } from "hooks/AppContext";
import { AppState, UserActionTypes } from "interfaces/context/appContextInterfaces";
import { JobLevel, JobLocation, SingleJob } from "interfaces/api/apiInterfaces";
import {
    genBaseProps, getJobTrackingProps, userTrack, museTrack
} from "utils/tracking";
import { getCarmenData } from "utils/location";
import { getActiveFeatureFlags } from "utils/featureFlags";
import { getCompressedJob } from "utils/orm";
import { getUser, searchJobs } from "utils/api";
import { useContentPercentViewed } from "hooks/useContentPercentViewed";
import { FeatureFlagsProvider } from "hooks/FeatureFlagsContext";
import { useSessionStorage } from "hooks/useSessionStorage";
import { JobIndividualPageLayout } from "components/JobIndividualPageLayout";
import { JobIndividualHeader } from "components/JobIndividualHeader";
import { JobIndividualBody } from "components/JobIndividualBody";
import { JobIndividualSidebar } from "components/JobIndividualSidebar";
import { JobPostJsonLd } from "components/JobPostJsonLd";
import { DirectApplyModal } from "components/DirectApplyModal";

import styles from "styles/Home.module.scss";
import { formatLog } from "utils/logging";
import { megaApplyCompanies } from "utils/constants";

interface InitialAppParams extends AppState {
    redirectPath?: string;
    queryString? : string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getServerSideProps = async (context: GetServerSidePropsContext) => {
    let notFound = false;
    const { job, company } = await getCompressedJob(context);

    const initialAppParams: InitialAppParams = {
        job,
        company
    };

    if ((initialAppParams?.job == null || initialAppParams?.company == null) && !initialAppParams.redirectPath) {
        notFound = true;
    }

    if (initialAppParams?.job !== null && initialAppParams?.job?.state !== "live") {
        context.res.statusCode = 404;
    }

    const redirectConfig = initialAppParams?.redirectPath ? {
        redirect: {
            destination: initialAppParams?.queryString ? `${initialAppParams?.redirectPath}?${initialAppParams.queryString}` : initialAppParams?.redirectPath,
            permanent: true
        }
    } : {};

    return {
        notFound,
        ...redirectConfig,
        props: {
            statusCode: context.res.statusCode,
            initialAppParams,
            featureFlags: {
                ...getActiveFeatureFlags(context)
            }
        }
    };
};

const setUserCarmenData = async (setUserLocation: any) => {
    try {
        const carmenResponse = await getCarmenData();
        if (carmenResponse) {
            setUserLocation(JSON.stringify(carmenResponse));
        }
    } catch (error) {
        const msg = `${process.env.APP_NAME} was unable to load location from Carmen.`;
        console.warn(msg, JSON.stringify(formatLog({ error })));
    }
};

const UserInit = (props: React.PropsWithChildren<{}>) => {
    const { children } = props;
    const { user } = useAppState();
    const [userLocation, setUserLocation] = useSessionStorage("user_location");
    const appDispatch = useAppDispatch();

    React.useEffect(() => {
        const setUser = async () => {
            try {
                const apiUser = await getUser();
                if (apiUser && apiUser.logged_in) {
                    appDispatch({
                        type: UserActionTypes.updateUser,
                        payload: {
                            ...apiUser,
                            location: userLocation ? JSON.parse(userLocation) : null
                        }
                    });
                } else {
                    appDispatch({ type: UserActionTypes.resetUser });
                }
            } catch (error) {
                const msg = "Job Renderer was unable retrieve the user.";
                console.warn(msg, JSON.stringify(formatLog({ error })));
            }
        };

        setUser().then(() => {
            // NOTE: An email with `themuse.com` does not mean the user is an admin
            const isMuser = user?.email && user.email.split("@")[1] === "themuse.com";
            userTrack({
                signed_in: user.logged_in ?? false,
                is_muser: isMuser,
                is_admin: isMuser,
                user_id: user.id ?? null
            });
        })
            .catch((error) => {
                userTrack({
                    signed_in: false,
                    is_muser: false,
                    is_admin: false,
                    user_id: null
                });
                console.error(error);
            });
    }, []);

    React.useEffect(() => {
        setUserCarmenData(setUserLocation);
    }, []);

    React.useEffect(() => {
        appDispatch({
            type: UserActionTypes.updateUser,
            payload: {
                ...user,
                location: userLocation ? JSON.parse(userLocation) : null
            }
        });
    }, [userLocation]);

    React.useEffect(() => {
        // NOTE: An email with `themuse.com` does not mean the user is an admin
        const isMuser = user?.email && user.email.split("@")[1] === "themuse.com";
        userTrack({
            signed_in: user?.logged_in ?? false,
            is_muser: isMuser,
            is_admin: isMuser,
            user_id: user?.id ?? null
        });
    }, [user]);

    return <>{children}</>;
};

/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
const handleSearchSubmit = () => {
    museTrack("Header Link Clicked", {
        ...genBaseProps(),
        header_link: "search_bar",
        link_name: "search_bar"
    });
};

const getSimilarJobs = async (job: SingleJob, callback: (similarJobsSearchResult: JobSearchResults) => void) => {
    if (job !== null) {
        const similarJobsRequest: JobSearchRequest = {
            query: job?.name?.split(" ").slice(0, 3).join(" "),
            level: job?.levels?.map((level: JobLevel) => level?.value === "senior" ? "senior,management" : level?.value).join(","),
            limit: 4
        };
        try {
            const similarJobsSearchResult = await searchJobs(similarJobsRequest);
            callback(similarJobsSearchResult);
        } catch (error) {
            const msg = `Job Renderer was unable fetch similar jobs for: ${job?.short_name}.`;
            console.warn(msg, JSON.stringify(formatLog({ error })));
        }
    }
};

const Home = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const trackedContentItems = useContentPercentViewed();
    const { publicRuntimeConfig: { museWebHost, apiBase } } = getConfig();
    const { initialAppParams, statusCode, featureFlags } = props;
    const initialAppState = initialAppParams;
    const pageLayoutProps: React.ComponentProps<typeof PageLayout> = {
        activePath: "/search",
        headerClassName: styles.pageHeader,
        pageName: styles.pageWrapper,
        mainClassName: styles.main,
        enableSearch: true,
        searchUrl: "/search",
        onSearchSubmit: handleSearchSubmit
    };
    const jobTrackingProps = getJobTrackingProps(initialAppState?.job, initialAppState?.company);
    const { job, company } = initialAppState;
    const physicalLocations = job?.locations?.filter((location) => location?.value !== "Flexible / Remote");
    const firstLocation = physicalLocations?.[0]?.value || "";
    const firstlocationDisplay = firstLocation ? ` located in ${firstLocation}, ` : ", ";
    const canonical = `${museWebHost}/jobs/${company?.short_name}/${job?.short_name}`;
    // eslint-disable-next-line max-len
    const description = `Find our ${job?.name} job description for ${company?.name}${firstlocationDisplay} as well as other career opportunities that the company is hiring for.`;
    const noIndex = job?.state !== "live";
    const title = `${job?.name} at ${company?.name} | The Muse`;

    // Mega Apply is small subset of companies.
    const isMegaApply: boolean = featureFlags.FEATURE_FLAG_DIRECT_APPLY_MEGA
        && megaApplyCompanies.includes(company.short_name);

    // The direct apply POC includes jobs from the Greenhouse ATS from companies that are limited.
    // We will have to exclude specific companies.
    const isIframeApply: boolean = featureFlags.FEATURE_FLAG_DIRECT_APPLY
        && job?.state === "live"
        && (company.state === "limited" || company.package === "Provisional")
        && job?.apply_link?.includes("greenhouse");

    // This controls th direct apply modal and is passed down to both the modal and the apply buttons.
    const [isDirectApplyModalOpen, setIsDirectApplyModalOpen] = React.useState<boolean>(false);
    // if we pass a function use it otherwise we are a normal link.
    const setIsOpen = isMegaApply || isIframeApply ? setIsDirectApplyModalOpen : null;

    React.useEffect(() => {
        if (trackedContentItems?.length > 0) {
            museTrack("Job Scrolled", {
                ...jobTrackingProps,
                ...trackedContentItems[trackedContentItems.length - 1]
            });
        }
    }, [trackedContentItems]);

    React.useEffect(() => {
        if (trackedContentItems?.length > 0) {
            museTrack("Job Scrolled", {
                ...jobTrackingProps,
                ...trackedContentItems[trackedContentItems.length - 1]
            });
        }
    }, [trackedContentItems]);

    const iframeApplyProps = {
        isOpen: isDirectApplyModalOpen,
        setIsOpen,
        link: job.apply_link
    };

    const megaApplyProps = {
        isOpen: isDirectApplyModalOpen,
        setIsOpen,
        link: job?.apply_link,
        jobId: `${job.id}`,
        companyId: `${company.id}`,
        jobName: job.name,
        companyName: company.name,
        locations: job.locations.map((loc: JobLocation) => loc.value),
        endpoint: `${apiBase}/mega_apply`
    };

    useEffect(() => {
        museTrack("Page Loaded", { status_code: statusCode });
        museTrack("Job Individual Page Loaded", jobTrackingProps);
    }, []);
    return (
        <FeatureFlagsProvider featureFlags={featureFlags}>
            <AppProvider {...initialAppState}>
                <NextSeo {...{
                    title,
                    description,
                    canonical,
                    openGraph: {
                        url: canonical,
                        title,
                        description,
                    },
                    noindex: noIndex
                }}
                />
                <JobPostJsonLd isDirectApply={isMegaApply || isIframeApply} />
                <UserInit>
                    <JobIndividualPageLayout {...pageLayoutProps}>
                        <JobIndividualHeader />
                        <JobIndividualBody
                            setIsOpen={setIsOpen}
                            isDirectApply={isMegaApply || isIframeApply}
                        />
                        <JobIndividualSidebar
                            getSimilarJobs={getSimilarJobs}
                            setIsOpen={setIsOpen}
                            isDirectApply={isMegaApply || isIframeApply}
                        />
                        {isMegaApply && (
                            <DirectApply {...megaApplyProps} />
                        )}
                        {!isMegaApply && isIframeApply && (
                            <DirectApplyModal {...iframeApplyProps} />
                        )}
                    </JobIndividualPageLayout>
                </UserInit>
            </AppProvider>
        </FeatureFlagsProvider>
    );
};

export default Home;
